import * as z from 'zod';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  sendContactUsMessageAPI,
  sendRequestToHostAPI,
  sendRequestToRoommateAPI,
} from 'api/messaging';
import { contactUsSchemaForm } from 'data/schemaForm';

type ContactUsData = z.infer<typeof contactUsSchemaForm>;

interface MessagingState {
  loading: boolean;
  error: boolean;
  success: boolean;
  /** Set when the server refused the request: limit of requests over 30 days reached. */
  limitReachedUntil: string | null;
  limit: number | null;
  /** Request refused for its content (contact details) or because the account is barred. */
  refusal: 'contact_info' | 'banned' | null;
}

const initialState: MessagingState = {
  loading: false,
  error: false,
  success: false,
  limitReachedUntil: null,
  limit: null,
  refusal: null,
};

interface ContactLimitRejection {
  reason: 'contact_limit';
  nextAllowedAt: string | null;
  limit: number | null;
}

export const sendContactUsMessage = createAsyncThunk(
  'messages/sendContactUsMessage',
  async (data: ContactUsData, { rejectWithValue }) => {
    try {
      const response = await sendContactUsMessageAPI(data);
      if (response.status === 204) return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendRequestToHost = createAsyncThunk(
  'messages/sendRequestToHost',
  async (data: MessageData, { rejectWithValue }) => {
    try {
      const response = await sendRequestToHostAPI(data);
      if (response.status === 204) return;
      if (response.status === 429) {
        const body = await response.json().catch(() => ({}));
        const rejection: ContactLimitRejection = {
          reason: 'contact_limit',
          nextAllowedAt: body?.nextAllowedAt || null,
          limit: body?.limit || null,
        };
        return rejectWithValue(rejection);
      }
      const body = await response.json().catch(() => ({}));
      if (response.status === 400 && body?.error === 'contact_info') {
        return rejectWithValue({ reason: 'contact_info' });
      }
      if (response.status === 403 && body?.error === 'messaging_banned') {
        return rejectWithValue({ reason: 'banned' });
      }
      return rejectWithValue({ reason: 'error', status: response.status });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendRequestToRoommate = createAsyncThunk(
  'messages/sendRequestToRoommate',
  async (data: MessageData, { rejectWithValue }) => {
    try {
      const response = await sendRequestToRoommateAPI(data);
      if (response.status === 204) return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const messagingSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.loading = false;
      state.error = false;
      state.success = false;
      state.limitReachedUntil = null;
      state.limit = null;
      state.refusal = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendContactUsMessage.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(sendContactUsMessage.fulfilled, (state) => {
        state.loading = false;
        state.error = false;
        state.success = true;
      })
      .addCase(sendContactUsMessage.rejected, (state) => {
        state.loading = false;
        state.error = true;
        state.success = false;
      })
      .addCase(sendRequestToHost.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(sendRequestToHost.fulfilled, (state) => {
        state.loading = false;
        state.error = false;
        state.success = true;
      })
      .addCase(sendRequestToHost.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.success = false;
        const payload = action.payload as ContactLimitRejection | { reason?: string } | undefined;
        if (payload?.reason === 'contact_info') state.refusal = 'contact_info';
        if (payload?.reason === 'banned') state.refusal = 'banned';
        if (payload?.reason === 'contact_limit' && 'nextAllowedAt' in payload) {
          state.limitReachedUntil = payload.nextAllowedAt || '';
          state.limit = payload.limit;
        }
      })
      .addCase(sendRequestToRoommate.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(sendRequestToRoommate.fulfilled, (state) => {
        state.loading = false;
        state.error = false;
        state.success = true;
      })
      .addCase(sendRequestToRoommate.rejected, (state) => {
        state.loading = false;
        state.error = true;
        state.success = false;
      });
  }
});

export const {
  resetStatus,
} = messagingSlice.actions;

export default messagingSlice.reducer;
